import React, { useState, useEffect, useRef } from 'react'
import {
  FormHelperText,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Checkbox,
  Grid,
  Box,
  Popper,
  Chip,
  Avatar,
  ClickAwayListener
} from '@mui/material'
import { useInput, useTranslate } from 'react-admin'
import get from 'lodash/get'
import SearchIcon from '@mui/icons-material/Search'

const CustomPopper = (props) => (
  <Popper
    {...props}
    modifiers={[
      {
        name: 'flip',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
        },
      },
    ]}
    placement='bottom-start'
  />
)

export const MultiAutocompleteInput = ({
  source,
  options = [],
  defaultValue,
  validate,
  onChange,
  fields = ['firstname', 'surname'], // Fields to be used for searching
  optionsText = ['long_name'],
  optionValue = 'id',
  separator = ' - ',
  helperText,
  multiple = false,
  onSearch,
  loading,
  resetOnLoad = true,
  disableClearable,
  startAdornment,
  positionOptionsText = null, // field to show specific options text ex: vi (position 0)
  useOptionValue = false,
  showAllOptionText = false, // field to show options text, separated by separator param, ex: vi | visa
  disablePortal = true,
  equalToValueByOptionValue = false, // field to compare by source value and by option value of each option in isOptionEqualToValue
  ...props
}) => {
  const emptyValue = multiple ? [] : null
  const translate = useTranslate()
  const [previousOptions, setPreviousOptions] = useState([])
  const [initialData, setInitialData] = useState()
  const [isSearching, setSearching] = useState(false)
  const [searchTerms, setSearchTerms] = useState(fields.reduce((acc, field) => ({ ...acc, [field]: '' }), {}))
  const { field, fieldState, isRequired } = useInput({
    source,
    validate,
    defaultValue: defaultValue || emptyValue,
  })
  const { error } = fieldState
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef(null);

  const handleClickAway = (event) => {
    const isInsideInput = wrapperRef.current?.contains(event.target);
    const isInsidePopper = document.querySelector('.MuiAutocomplete-popper')?.contains(event.target);

  if (!isInsideInput && !isInsidePopper && !props.multiple) {
    setOpen(false);
  }
  };

  useEffect(() => {
    if (!loading && options.length > 0 && isSearching) {
      setOpen(true)
      setSearching(false)
    }
  }, [loading, options, field?.value, isSearching])

  useEffect(() => {
    if (options.length > 0 && defaultValue && !initialData && !loading) {
      const selectedOption = options.find((option) => option[optionValue] === defaultValue)
      if (selectedOption) {
        setInitialData(selectedOption)
        field.onChange(selectedOption)
      }
    }

    if (JSON.stringify(previousOptions) !== JSON.stringify(options) && resetOnLoad && !defaultValue) {
      if (multiple) {
        const value = field.value || []
        field.onChange(
          value?.filter((elem) => options.find((option) => option[optionValue] === elem[optionValue]))
        )
        onChange && onChange({ target: { value: value } })
      } else if (!options.find((elem) => elem[optionValue] === field.value[optionValue])) {
        field.onChange('')
        onChange && onChange({ target: { value: '' } })
      }
    }
    setPreviousOptions(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultValue, loading])

  const getText = (item, positionOptionsText = null, useOptionValue = false, showAllOptionText = false) => {
    if (item) {
      if (positionOptionsText !== null && !showAllOptionText) {
        if (useOptionValue) {
          const selected = options.find((option) => option?.[optionValue] === item)
          if (selected) {
            return selected[optionsText?.[positionOptionsText]]
          }
        }

        return item?.[optionsText?.[positionOptionsText]]
      }

      if (showAllOptionText) {
        if (useOptionValue) {
          const selected = options.find((option) => option?.[optionValue] === item)
          if (selected) {
            return optionsText
              ? optionsText.map((option) => get(selected, option)).join(separator)
              : selected[optionsText?.[0]]
          }
        }

        return optionsText
          ? optionsText.map((option) => get(item, option)).join(separator)
          : item[optionsText?.[0]]
      }

      return optionsText ? optionsText.map((option) => get(item, option)).join(separator) : item?.name
    }

    return ''
  }

  const onChangeField = (value) => {
    if (useOptionValue) {
      const data = value[optionValue]
      field.onChange(data)
      onChange && onChange({ target: { data } })
    } else {
      field.onChange(value)
      onChange && onChange({ target: { value } })
    }
    // Reset search fields after selecting an option
    setSearchTerms(fields.reduce((acc, field) => ({ ...acc, [field]: '' }), {}))
  }

  const handleSearchChange = (field, value) => {
    setSearchTerms((prev) => ({ ...prev, [field]: value }))
    const searchQuery = { ...searchTerms, [field]: value }
    onSearch && onSearch(searchQuery) // Pass the entire search terms object to the search function
    setSearching(true)
  }

  return (
    <>
      <Box mb={2}>
        <Grid container>
          {fields.map((field) => (
            <Grid item xs={12} sm={fields.length === 1 ? 12 : 6} key={field} sx={{ paddingX: '2px', marginY: '5px' }}>
              <TextField
                variant='outlined'
                size='small'
                value={searchTerms[field]}
                onChange={(e) => handleSearchChange(field, e.target.value)}
                placeholder={translate(`ra.field.${field}`)}
                fullWidth
                disabled={disableClearable}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <ClickAwayListener onClickAway={handleClickAway}>
      <Autocomplete
        {...props}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={multiple ? field.value || [] : field.value || null}
        multiple={multiple}
        ref={wrapperRef}
        size='small'
        options={options}
        onChange={(e, value) => onChangeField(value)}
        getOptionLabel={(e) => getText(e, positionOptionsText, useOptionValue, showAllOptionText)}
        isOptionEqualToValue={(option, value) => {
          if (equalToValueByOptionValue) {
            return get(option, optionValue) == value
          }
          return getText(option) === getText(value)
        }}
        disableClearable={isRequired || disableClearable}
        noOptionsText={translate('ra.validation.noOptionsText')}
        loading={loading}
        disabled={options.length === 0 || disableClearable}
        loadingText={translate('ra.action.loading')}
        disablePortal={disablePortal}
        disableCloseOnSelect={multiple}
        PopperComponent={CustomPopper}
        renderTags={
          props.renderTags
            ? (value, getTagProps) => {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    variant='outlined'
                    label={getText(option)}
                    avatar={option?.symbol && <Avatar>{option?.symbol}</Avatar>}
                    style={{ backgroundColor: option?.color }}
                    size='small'
                  />
                ))
              }
            : null
        }
        renderOption={(props, option, { selected }) => (
          <Box component='li' {...props}>
            {multiple && <Checkbox checked={selected} style={{ marginRight: 8 }} />}
            {getText(option)}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(error)}
            variant='outlined'
            label=''
            placeholder='Seleccione una opción...'
            disabled={disableClearable || options.length === 0}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment && <InputAdornment position='start'>{startAdornment}</InputAdornment>}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      </ClickAwayListener>
      <FormHelperText className='form-helper-text' error={error?.message ? true : false}>
        {error?.message || helperText}
      </FormHelperText>
    </>
  )
}
